import { useMemo } from "react";
import { lagersOf, reasonLabel, stockLines } from "@lagerlog/core";
import type { Booking } from "@lagerlog/core";
import { Containers, euro } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { Lines, Parts } from "./Stock.tsx";
import { SubNav } from "./SubNav.tsx";
import { useWide } from "../wide.ts";
import { fmt } from "./Book.tsx";

const KIND: Record<string, string> = { out: "Entnommen", in: "Eingelagert", move: "Umgelagert", count: "Gezählt" };
const clock = (at: number) => new Date(at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

/**
 * The day at a glance for the owner (persona P1: "abends sehen, was heute rausging"): stock value,
 * what went out and in today, today's count differences, and every movement of the day. Everything
 * is computed from the journal on this device, for the storages it holds.
 */
export function Overview() {
  const { store, version } = useStore();
  const catalog = useCatalog();
  const drinks = useDrinks();
  const ledger = useLedger();
  const wide = useWide();

  const day = useMemo(() => {
    if (!ledger || !store.replica || !catalog) return null;
    const art = (id: string) => catalog.articles.get(id);
    const scope = new Set(store.config?.lagers ?? []);
    const today = new Date().setHours(0, 0, 0, 0);
    // Kegs and cans apart from bottles (Containers).
    const byLager = new Map<string, { bottles: Containers; value: number }>();
    const stock = new Containers();
    for (const l of stockLines(ledger)) {
      if (!scope.has(l.lager)) continue;
      const s = byLager.get(l.lager) ?? { bottles: new Containers(), value: 0 };
      s.bottles.add(art(l.article), l.qty);
      stock.add(art(l.article), l.qty);
      s.value += l.value;
      byLager.set(l.lager, s);
    }
    const moves: Booking[] = [];
    for (const b of store.replica.journal()) {
      if (b.at < today || ledger.cancelled.has(b.id) || b.kind === "cancel" || b.kind === "convert") continue;
      if (lagersOf(b).some((l) => scope.has(l))) moves.push(b);
    }
    moves.reverse();
    const out = { bottles: new Containers(), value: 0 };
    const into = { bottles: new Containers(), value: 0 };
    const reasons = new Map<string, { bottles: Containers; value: number }>();
    for (const b of moves) {
      if (b.kind === "out") {
        const value = ledger.valuations.get(b.id)?.value ?? 0;
        out.bottles.add(art(b.article), b.qty);
        out.value += value;
        const r = reasons.get(b.reason) ?? { bottles: new Containers(), value: 0 };
        r.bottles.add(art(b.article), b.qty);
        r.value += value;
        reasons.set(b.reason, r);
      } else if (b.kind === "in") {
        into.bottles.add(art(b.article), b.qty);
        into.value += b.amount ?? 0;
      }
    }
    const counts = [...ledger.counts.values()].filter((c) => c.at >= today && scope.has(c.lager) && c.diff !== 0);
    return {
      byLager,
      stock,
      value: [...byLager.values()].reduce((s, l) => s + l.value, 0),
      moves,
      out,
      into,
      reasons: [...reasons.entries()].sort((a, b) => b[1].bottles.total - a[1].bottles.total),
      diff: { positions: counts.length, value: counts.reduce((s, c) => s + c.value, 0) },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledger, version]);

  if (!catalog || !ledger || !day) return null;
  const name = (id: string) => catalog.articles.get(id)?.name ?? id;
  const place = (id: string) => {
    const l = catalog.locations.get(id);
    return l ? `${catalog.lagers.get(l.lager)?.name ?? l.lager} · ${l.name}` : id;
  };

  const detailOf = (b: Booking) =>
    b.kind === "move" ? `${place(b.from)} → ${place(b.to)}` : b.kind === "count" ? place(b.location) : b.kind === "in" || b.kind === "out" ? reasonLabel(b.reason, catalog.articles.get(b.article)?.fields) : "";
  const who = (b: Booking) => store.personName(b.person) ?? b.device ?? "";

  // Staff and viewers see quantities only (F20).
  const prices = store.prices;
  const kpis = (
      <div className="kpis">
        <div className="kpi">
          <span className="muted">{prices ? "Lagerwert" : "Bestand"}</span>
          <strong>{prices ? euro(day.value) : day.stock.text()}</strong>
          <span className="muted">{prices ? "zum Einkaufspreis" : "in deinen Lagern"}</span>
        </div>
        <div className="kpi">
          <span className="muted">Heute raus</span>
          <strong>{day.out.bottles.text()}</strong>
          {prices && <span className="muted">{euro(day.out.value)} Einkaufswert</span>}
        </div>
        <div className="kpi">
          <span className="muted">Heute rein</span>
          <strong>{day.into.bottles.text()}</strong>
          {prices && <span className="muted">{euro(day.into.value)}</span>}
        </div>
        <div className={`kpi ${prices && day.diff.value < 0 ? "negative" : ""}`}>
          <span className="muted">Differenzen heute</span>
          <strong>{prices ? euro(day.diff.value) : day.diff.positions}</strong>
          <span className="muted">
            {day.diff.positions} {day.diff.positions === 1 ? "Position" : "Positionen"}
          </span>
        </div>
      </div>
  );

  const reasons = (
          <div className="card">
            <h3>Heute raus nach Grund</h3>
            {day.reasons.length === 0 ? (
              <p className="muted">Noch nichts entnommen.</p>
            ) : (
              <ul className="list">
                {day.reasons.map(([id, r]) => (
                  <li key={id} className="row between">
                    <span>{reasonLabel(id)}</span>
                    <span className="side">
                      <Lines parts={r.bottles.parts()} />
                      {prices && <span className="muted">{euro(r.value)}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
  );

  if (!wide)
    return (
      <div className="overview narrow">
        <SubNav current="uebersicht" />
        {kpis}
        <section>
          <h2>Bewegungen heute</h2>
          {day.moves.length === 0 ? (
            <p className="muted">Heute ist noch nichts gebucht.</p>
          ) : (
            <ul className="list">
              {day.moves.map((b) => {
                if (b.kind === "cancel" || b.kind === "convert") return null;
                return (
                  <li key={b.id} className="entry-block">
                    <span className="muted">
                      {clock(b.at)} · {who(b)} · {KIND[b.kind]}
                    </span>
                    <br />
                    {fmt(catalog.articles.get(b.article), b.qty)} {name(b.article)}
                    <br />
                    <span className="muted">{detailOf(b)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        {reasons}
      </div>
    );

  return (
    <div className="overview">
      {kpis}

      <div className="split wide-left">
        <section className="pane">
          <h2>Bewegungen heute</h2>
          {day.moves.length === 0 ? (
            <p className="muted">Heute ist noch nichts gebucht.</p>
          ) : (
            <table className="grid">
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Wer</th>
                  <th>Was</th>
                  <th>{drinks ? "Getränk" : "Wein"}</th>
                  <th>Menge</th>
                  <th>Grund oder Ort</th>
                </tr>
              </thead>
              <tbody>
                {day.moves.map((b) => {
                  if (b.kind === "cancel" || b.kind === "convert") return null;
                  const a = catalog.articles.get(b.article);
                  const detail =
                    b.kind === "move"
                      ? `${place(b.from)} → ${place(b.to)}`
                      : b.kind === "count"
                        ? place(b.location)
                        : reasonLabel(b.reason, a?.fields);
                  return (
                    <tr key={b.id}>
                      <td>{clock(b.at)}</td>
                      <td>{who(b)}</td>
                      <td>{KIND[b.kind]}</td>
                      <td>{name(b.article)}</td>
                      <td className="stack-cell">
                        <Parts a={a} qty={b.qty} />
                      </td>
                      <td className={b.kind === "move" ? "" : "nowrap"}>{detail}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
        <aside className="pane side-pane">
          <div className="card">
            <h3>Heute raus nach Grund</h3>
            {day.reasons.length === 0 ? (
              <p className="muted">Noch nichts entnommen.</p>
            ) : (
              <table className="grid">
                <tbody>
                  {day.reasons.map(([id, r]) => (
                    <tr key={id}>
                      <td>{reasonLabel(id)}</td>
                      <td className="num stack-cell">
                        <Lines parts={r.bottles.parts()} />
                      </td>
                      {prices && <td className="num">{euro(r.value)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="card">
            <h3>{prices ? "Lagerwert je Lager" : "Bestand je Lager"}</h3>
            <table className="grid">
              <tbody>
                {[...day.byLager.entries()].map(([id, l]) => (
                  <tr key={id}>
                    <td>{catalog.lagers.get(id)?.name ?? id}</td>
                    <td className="num stack-cell">
                      <Lines parts={l.bottles.parts()} />
                    </td>
                    {prices && <td className="num">{euro(l.value)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </aside>
      </div>
    </div>
  );
}
